import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  documentId,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/providers/AuthProvider';
import { addNotification } from '@/services/notificationService';

interface FirestoreUserProfile {
  name?: string;
  displayName?: string;
  photoURL?: string | null;
  username?: string | null;
  isOnline?: boolean;
}

interface FriendshipDocData {
  participants?: string[];
  senderId?: string;
  receiverId?: string;
  status?: string;
  createdAt?: string;
  acceptedAt?: string;
}

export interface FriendUser {
  id: string;
  name: string;
  photoURL?: string | null;
  username?: string | null;
  isOnline: boolean;
}

export interface FriendRecord extends FriendshipDocData {
  id: string;
  name?: string;
  photoURL?: string | null;
  receiverName?: string;
  receiverPhoto?: string | null;
  otherUser: FriendUser;
}

export interface PendingFriendRequest {
  id: string;
  senderId: string;
  receiverId?: string;
  senderName: string;
  senderPhoto?: string | null;
  senderUsername?: string | null;
  createdAt?: string;
  status: string;
}

export interface FriendReconnectCandidate {
  friendshipId: string;
  friend: FriendUser;
  daysConnected: number;
  reconnectScore: number;
  reason: string;
}

export interface FriendsInsights {
  totalFriends: number;
  onlineNow: number;
  pendingIncoming: number;
  recentConnections30d: number;
  relationshipHealthScore: number;
  reconnectCandidates: FriendReconnectCandidate[];
}

interface UseFriendsReturn {
  friends: FriendRecord[];
  pendingRequests: PendingFriendRequest[];
  insights: FriendsInsights;
  loading: boolean;
  error: string | null;
  sendFriendRequest: (receiverId: string) => Promise<void>;
  acceptFriendRequest: (friendshipId: string, senderId: string) => Promise<void>;
  removeFriend: (friendshipId: string) => Promise<void>;
}

const USERS_BATCH_SIZE = 10;

function getOtherUserId(participants: string[] | undefined, currentUserId: string): string | null {
  if (!Array.isArray(participants)) return null;
  const otherUserId = participants.find((id) => id !== currentUserId);
  return typeof otherUserId === 'string' && otherUserId ? otherUserId : null;
}

function normalizeUserProfile(userId: string, profile?: FirestoreUserProfile): FriendUser {
  return {
    id: userId,
    name: profile?.name || profile?.displayName || 'Unknown User',
    photoURL: profile?.photoURL || null,
    username: profile?.username || null,
    isOnline: Boolean(profile?.isOnline),
  };
}

async function fetchUsersByIds(userIds: string[]): Promise<Record<string, FirestoreUserProfile>> {
  const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
  if (uniqueUserIds.length === 0) return {};

  const userProfiles: Record<string, FirestoreUserProfile> = {};

  for (let i = 0; i < uniqueUserIds.length; i += USERS_BATCH_SIZE) {
    const chunk = uniqueUserIds.slice(i, i + USERS_BATCH_SIZE);
    const usersQuery = query(collection(db, 'users'), where(documentId(), 'in', chunk));
    const usersSnapshot = await getDocs(usersQuery);

    usersSnapshot.docs.forEach((userDoc) => {
      userProfiles[userDoc.id] = userDoc.data() as FirestoreUserProfile;
    });
  }

  return userProfiles;
}

async function buildFriendshipLists(
  docs: QueryDocumentSnapshot<DocumentData>[],
  currentUserId: string
): Promise<{ friends: FriendRecord[]; pendingRequests: PendingFriendRequest[] }> {
  const mappedDocs = docs.map((snapshotDoc) => {
    const data = snapshotDoc.data() as FriendshipDocData;
    const otherUserId = getOtherUserId(data.participants, currentUserId);

    return {
      id: snapshotDoc.id,
      data,
      otherUserId,
    };
  });

  const otherUserIds = mappedDocs
    .map((mappedDoc) => mappedDoc.otherUserId)
    .filter((id): id is string => Boolean(id));

  const usersById = await fetchUsersByIds(otherUserIds);

  const friends: FriendRecord[] = [];
  const pendingRequests: PendingFriendRequest[] = [];

  mappedDocs.forEach(({ id, data, otherUserId }) => {
    if (!otherUserId) return;

    const otherUser = normalizeUserProfile(otherUserId, usersById[otherUserId]);
    const status = data.status || '';

    if (status === 'accepted') {
      friends.push({
        id,
        ...data,
        otherUser,
      });
      return;
    }

    if (status === 'pending') {
      // Check both senderId and receiverId patterns for backward compatibility.
      const isReceiver = data.receiverId === currentUserId || data.senderId !== currentUserId;
      if (!isReceiver) return;

      pendingRequests.push({
        id,
        senderId: data.senderId || otherUserId,
        senderName: otherUser.name,
        senderPhoto: otherUser.photoURL,
        senderUsername: otherUser.username,
        createdAt: data.createdAt,
        status: status || 'pending',
      });
    }
  });

  friends.sort((a, b) => {
    const aName = a.otherUser.name.toLowerCase();
    const bName = b.otherUser.name.toLowerCase();
    return aName.localeCompare(bName);
  });

  pendingRequests.sort((a, b) => {
    const aTime = Date.parse(a.createdAt || '');
    const bTime = Date.parse(b.createdAt || '');
    const safeA = Number.isFinite(aTime) ? aTime : 0;
    const safeB = Number.isFinite(bTime) ? bTime : 0;
    return safeB - safeA;
  });

  return { friends, pendingRequests };
}

export function useFriends(refreshKey = 0): UseFriendsReturn {
  const [friends, setFriends] = useState<FriendRecord[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingFriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const insights = useMemo<FriendsInsights>(() => {
    if (!friends.length) {
      return {
        totalFriends: 0,
        onlineNow: 0,
        pendingIncoming: pendingRequests.length,
        recentConnections30d: 0,
        relationshipHealthScore: 0,
        reconnectCandidates: [],
      };
    }

    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const daysSince = (dateRaw?: string) => {
      if (!dateRaw) return 365;
      const parsed = Date.parse(dateRaw);
      if (!Number.isFinite(parsed)) return 365;
      return Math.max(1, Math.floor((now - parsed) / dayMs));
    };

    const onlineNow = friends.filter((friendship) => friendship.otherUser.isOnline).length;
    const recentConnections30d = friends.filter((friendship) => daysSince(friendship.createdAt) <= 30).length;

    const reconnectCandidates = friends
      .map((friendship) => {
        const daysConnected = daysSince(friendship.createdAt);
        const offlineBoost = friendship.otherUser.isOnline ? 0 : 12;
        const ageBoost = Math.min(24, Math.round(daysConnected / 7));
        const pendingBoost = pendingRequests.length > 0 ? 2 : 0;
        const reconnectScore = Math.max(0, Math.min(100, offlineBoost + ageBoost + pendingBoost));

        const reason = friendship.otherUser.isOnline
          ? 'long-term friend worth checking in'
          : daysConnected > 90
            ? 'quiet connection that may need a ping'
            : 'recently inactive connection';

        return {
          friendshipId: friendship.id,
          friend: friendship.otherUser,
          daysConnected,
          reconnectScore,
          reason,
        };
      })
      .filter((candidate) => candidate.reconnectScore >= 14)
      .sort((a, b) => b.reconnectScore - a.reconnectScore)
      .slice(0, 5);

    const onlineRate = onlineNow / Math.max(1, friends.length);
    const recentRate = recentConnections30d / Math.max(1, friends.length);
    const reconnectRisk = reconnectCandidates.length / Math.max(1, friends.length);

    const relationshipHealthScore = Math.max(0, Math.min(100, Math.round(
      onlineRate * 55 + recentRate * 25 + (1 - reconnectRisk) * 20
    )));

    return {
      totalFriends: friends.length,
      onlineNow,
      pendingIncoming: pendingRequests.length,
      recentConnections30d,
      relationshipHealthScore,
      reconnectCandidates,
    };
  }, [friends, pendingRequests.length]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setFriends([]);
      setPendingRequests([]);
      return;
    }

    setLoading(true);
    setError(null);
    let cancelled = false;

    // Set up real-time listener for friendships
    const friendshipsQuery = query(
      collection(db, 'friendships'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(friendshipsQuery, async (snapshot) => {
      try {
        const lists = await buildFriendshipLists(snapshot.docs, user.uid);
        if (cancelled) return;

        setFriends(lists.friends);
        setPendingRequests(lists.pendingRequests);
        setLoading(false);
      } catch (error) {
        console.error('Error in friendships listener:', error);
        if (cancelled) return;

        setError('Failed to load friends. Please try again.');
        setLoading(false);
      }
    }, (error) => {
      const errorCode = String((error as any)?.code || '').toLowerCase();
      const isPermissionDenied = errorCode.includes('permission-denied');

      if (!isPermissionDenied) {
        console.error('Friendships listener error:', error);
      }

      if (cancelled) return;

      setError(isPermissionDenied ? 'Sign in to load friends.' : 'Failed to load friends. Please try again.');
      if (isPermissionDenied) {
        setFriends([]);
        setPendingRequests([]);
      }
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.uid, refreshKey]);

  const sendFriendRequest = async (receiverId: string) => {
    if (!user) throw new Error('User not authenticated');
    if (receiverId === user.uid) throw new Error('Cannot send a friend request to yourself');

    try {
      // Fetch current user's data
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};

      // Check if a friendship already exists
      const existingQuery = query(
        collection(db, 'friendships'),
        where('participants', 'array-contains', user.uid)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      const alreadyFriends = existingSnapshot.docs.some(doc => {
        const data = doc.data();
        return data.participants.includes(receiverId);
      });

      if (alreadyFriends) {
        throw new Error('Friendship or request already exists');
      }

      // Create new friendship request
      const friendshipRef = collection(db, 'friendships');
      const newFriendship = await addDoc(friendshipRef, {
        senderId: user.uid,
        senderName: user.displayName || 'Someone',
        senderPhoto: user.photoURL,
        senderUsername: userData?.username || user.email?.split('@')[0],
        receiverId,
        status: 'pending',
        participants: [user.uid, receiverId],
        createdAt: new Date().toISOString()
      });

      // Add notification for receiver
      await addNotification(receiverId, 'friend_request', {
        senderId: user.uid,
        senderName: user.displayName || 'Someone',
        senderPhoto: user.photoURL,
        senderUsername: userData?.username || user.email?.split('@')[0],
        friendshipId: newFriendship.id,
        actionUrl: userData?.username || user.email?.split('@')[0]
          ? `/profile/${encodeURIComponent(userData?.username || user.email?.split('@')[0])}`
          : undefined
      });

      // Real-time listener will automatically update the friends list
      return;
    } catch (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }
  };

  const acceptFriendRequest = async (friendshipId: string, senderId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Fetch accepter's username for deep-linking
      let accepterUsername: string | undefined;
      try {
        const accepterSnap = await getDoc(doc(db, 'users', user.uid));
        const accepterData = accepterSnap.exists() ? (accepterSnap.data() as FirestoreUserProfile) : undefined;
        if (typeof accepterData?.username === 'string' && accepterData.username.trim()) {
          accepterUsername = accepterData.username.trim();
        }
      } catch {
        // ignore
      }

      const friendshipRef = doc(db, 'friendships', friendshipId);
      await updateDoc(friendshipRef, {
        status: 'accepted',
        acceptedAt: new Date().toISOString()
      });

      await addNotification(senderId, 'friend_accepted', {
        accepterId: user.uid,
        accepterName: user.displayName || 'Someone',
        accepterPhoto: user.photoURL,
        accepterUsername,
        friendshipId,
        actionUrl: accepterUsername ? `/profile/${encodeURIComponent(accepterUsername)}` : undefined
      });
      // Real-time listener will automatically update the friends list
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  };

  const removeFriend = async (friendshipId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      await deleteDoc(doc(db, 'friendships', friendshipId));
      // Real-time listener will automatically update the friends list
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  };

  return {
    friends,
    pendingRequests,
    insights,
    loading,
    error,
    sendFriendRequest,
    acceptFriendRequest,
    removeFriend
  };
}